import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FiClock,
  FiPlus,
  FiTrash2,
  FiChevronUp,
  FiChevronDown,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import "./workingDaysPage.css";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;
const WORKDAYS_GET = `${BASE_URL}/service/my-workdays/`;
const WORKDAYS_CREATE = `${BASE_URL}/service/workdays-create/`;
const WORKDAYS_UPDATE = `${BASE_URL}/service/workdays-update/`;

const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const daysList = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const defaultTimes = { start: "09:00AM", end: "06:00PM" };
const defaultTimesApi = { start_time: "9:00am", end_time: "6:00pm" };

const TIME_STEP = 5;
const MIN_GAP = 60;
const DEFAULT_RANGE_DURATION = 60;
const END_OF_DAY_MINUTES = 23 * 60 + 59;

const makeKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

const parseTimeToDate = (timeStr) => {
  if (!timeStr) return null;

  const raw = String(timeStr).trim();

  if (/am|pm/i.test(raw)) {
    const timeStrLower = raw.toLowerCase();
    const [timePart, meridian] = [
      timeStrLower.slice(0, -2),
      timeStrLower.slice(-2),
    ];

    let [hours, minutes] = timePart.split(":").map(Number);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    if (meridian === "pm" && hours !== 12) {
      hours += 12;
    } else if (meridian === "am" && hours === 12) {
      hours = 0;
    }

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  const [hours, minutes] = raw.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const toPm = (date) => {
  if (!date || !(date instanceof Date)) return "9:00am";

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const meridian = hours >= 12 ? "pm" : "am";

  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;

  return `${hours}:${minutes.toString().padStart(2, "0")}${meridian}`;
};

const timeToMinutes = (timeStr) => {
  const date = parseTimeToDate(timeStr);
  if (!date) return 0;
  return date.getHours() * 60 + date.getMinutes();
};

const minutesToTime = (mins) => {
  const safe = Math.max(0, Math.min(END_OF_DAY_MINUTES, mins));
  const date = new Date();
  date.setHours(Math.floor(safe / 60), safe % 60, 0, 0);
  return toPm(date).toUpperCase();
};

const convertTo24HourFormat = (timeStr) => {
  const date = parseTimeToDate(timeStr);
  if (!date) return "00:00:00";

  return `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}:00`;
};

const toAmPm = (time24) => {
  const date = parseTimeToDate(time24);
  if (!date) return "";
  return toPm(date).toUpperCase();
};

const to24 = (timeAmPm) => {
  const date = parseTimeToDate(timeAmPm);
  if (!date) return "00:00";

  return `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
};

const hasOverlap = (ranges) => {
  if (ranges.length <= 1) return false;

  const sorted = [...ranges].sort(
    (a, b) => timeToMinutes(a.start) - timeToMinutes(b.start)
  );

  for (let i = 0; i < sorted.length - 1; i++) {
    const endA = timeToMinutes(sorted[i].end);
    const startB = timeToMinutes(sorted[i + 1].start);

    if (endA > startB) return true;
  }

  return false;
};

const syncRangesForward = (ranges, changedIndex) => {
  const nextRanges = [...ranges];

  for (let i = changedIndex + 1; i < nextRanges.length; i++) {
    const prev = nextRanges[i - 1];
    const current = nextRanges[i];

    const prevEnd = timeToMinutes(prev.end);
    let currentStart = timeToMinutes(current.start);
    let currentEnd = timeToMinutes(current.end);

    const requiredStart = prevEnd + MIN_GAP;
    const currentDuration = Math.max(1, currentEnd - currentStart);

    if (currentStart < requiredStart) {
      currentStart = requiredStart;
      currentEnd = currentStart + currentDuration;
    }

    if (currentEnd > END_OF_DAY_MINUTES) {
      currentEnd = END_OF_DAY_MINUTES;
      currentStart = Math.max(requiredStart, currentEnd - currentDuration);
    }

    if (currentStart >= currentEnd) {
      currentStart = requiredStart;
      currentEnd = Math.min(
        currentStart + DEFAULT_RANGE_DURATION,
        END_OF_DAY_MINUTES
      );
    }

    nextRanges[i] = {
      ...current,
      start: minutesToTime(currentStart),
      end: minutesToTime(currentEnd),
    };
  }

  return nextRanges;
};

const validateRangesWithBoundaries = (ranges) => {
  for (let i = 0; i < ranges.length; i++) {
    const current = ranges[i];
    const startMins = timeToMinutes(current.start);
    const endMins = timeToMinutes(current.end);

    if (endMins > END_OF_DAY_MINUTES || startMins > END_OF_DAY_MINUTES) {
      return "Work time cannot go past 11:59 PM.";
    }

    if (endMins <= startMins) {
      return "End time must be later than start time.";
    }

    if (i > 0) {
      const prevEnd = timeToMinutes(ranges[i - 1].end);
      const minAllowedStart = prevEnd + MIN_GAP;

      if (startMins < minAllowedStart) {
        return "This time range cannot start before the previous one ends.";
      }
    }
  }

  if (hasOverlap(ranges)) {
    return "You have already registered for service at this time.";
  }

  return null;
};

const canAddTimeRangeByRanges = (ranges) => {
  if (!ranges?.length) return false;

  const lastRange = ranges[ranges.length - 1];
  const lastEnd = timeToMinutes(lastRange.end);
  const nextStart = lastEnd + MIN_GAP;

  return nextStart <= END_OF_DAY_MINUTES;
};

export default function WorkingDaysPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const userStatus = localStorage.getItem("user_status");

  const [schedule, setSchedule] = useState(
    days.map((day) => ({
      name: day,
      enabled: false,
      dayTimeRanges: [
        {
          _k: makeKey(),
          start: defaultTimes.start,
          end: defaultTimes.end,
          isBreak: false,
        },
      ],
    }))
  );

  useEffect(() => {
    const timeZone = getUserTimeZone();
    setUserTimeZone(timeZone);
    localStorage.setItem("user_timezone", timeZone);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem("working_days_closed", "true");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    const workingDaysCode =
      userStatus === "salon" ? "230" : userStatus === "worker" ? "236" : "218";

    localStorage.setItem("onboarding_code", workingDaysCode);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [userStatus]);

  useEffect(() => {
    fetchWorkdays();
  }, []);

  const toggleDay = (index) => {
    setSchedule((prev) =>
      prev.map((day, i) => {
        if (i !== index) return day;

        const newEnabled = !day.enabled;

        return {
          ...day,
          enabled: newEnabled,
          dayTimeRanges: newEnabled
            ? day.dayTimeRanges?.length
              ? day.dayTimeRanges
              : [
                  {
                    _k: makeKey(),
                    start: defaultTimes.start,
                    end: defaultTimes.end,
                    isBreak: false,
                  },
                ]
            : [],
        };
      })
    );
  };

  const updateRangeValue = (dayIndex, rangeIndex, type, value) => {
    let showError = null;

    setSchedule((prev) => {
      const updated = [...prev];
      const selectedDay = { ...updated[dayIndex] };
      let newRanges = [...selectedDay.dayTimeRanges];

      newRanges[rangeIndex] = {
        ...newRanges[rangeIndex],
        [type]: value,
      };

      const current = newRanges[rangeIndex];
      const startTime = parseTimeToDate(current.start)?.getTime() || 0;
      const endTime = parseTimeToDate(current.end)?.getTime() || 0;
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);
      const endOfDayTime = endOfDay.getTime();

      if (endTime > endOfDayTime || startTime > endOfDayTime) {
        showError = "Work time cannot go past 11:59 PM.";
        return prev;
      }

      if (endTime <= startTime) {
        showError = "End time must be later than start time.";
        return prev;
      }

      if (rangeIndex > 0) {
        const prevEnd = timeToMinutes(newRanges[rangeIndex - 1].end);
        const minAllowedStart = prevEnd + MIN_GAP;

        if (type === "start" && timeToMinutes(current.start) < minAllowedStart) {
          showError = "This time range cannot start before the previous one ends.";
          return prev;
        }
      }

      newRanges = syncRangesForward(newRanges, rangeIndex);

      const validationError = validateRangesWithBoundaries(newRanges);
      if (validationError) {
        showError = validationError;
        return prev;
      }

      selectedDay.dayTimeRanges = newRanges;
      updated[dayIndex] = selectedDay;
      return updated;
    });

    if (showError) {
      setTimeout(() => toast.error(showError), 0);
    }
  };

  const handleTimeChange = (dayIndex, rangeIndex, type, value24) => {
    const formatted = toAmPm(value24);
    if (!formatted) return;
    updateRangeValue(dayIndex, rangeIndex, type, formatted);
  };

  const shiftTime = (dayIndex, rangeIndex, type, deltaMinutes) => {
    let showError = null;

    setSchedule((prev) => {
      const updated = [...prev];
      const selectedDay = { ...updated[dayIndex] };
      let newRanges = [...selectedDay.dayTimeRanges];

      const current = newRanges[rangeIndex];
      const currentValue = type === "start" ? current.start : current.end;
      let nextMinutes = timeToMinutes(currentValue) + deltaMinutes;

      if (nextMinutes < 0) nextMinutes = 0;

      if (nextMinutes > END_OF_DAY_MINUTES) {
        showError = "Work time cannot go past 11:59 PM.";
        return prev;
      }

      const nextFormatted = minutesToTime(nextMinutes);

      newRanges[rangeIndex] = {
        ...current,
        [type]: nextFormatted,
      };

      const changed = newRanges[rangeIndex];
      const changedStart = timeToMinutes(changed.start);
      const changedEnd = timeToMinutes(changed.end);

      if (changedEnd <= changedStart) {
        showError = "End time must be later than start time.";
        return prev;
      }

      if (rangeIndex > 0) {
        const prevEnd = timeToMinutes(newRanges[rangeIndex - 1].end);
        const minAllowedStart = prevEnd + MIN_GAP;

        if (type === "start" && changedStart < minAllowedStart) {
          showError = "This time range cannot start before the previous one ends.";
          return prev;
        }
      }

      newRanges = syncRangesForward(newRanges, rangeIndex);

      const validationError = validateRangesWithBoundaries(newRanges);
      if (validationError) {
        showError = validationError;
        return prev;
      }

      selectedDay.dayTimeRanges = newRanges;
      updated[dayIndex] = selectedDay;
      return updated;
    });

    if (showError) {
      setTimeout(() => toast.error(showError), 0);
    }
  };

  const handleTimeKeyDown = (e, dayIndex, rangeIndex, type) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      shiftTime(dayIndex, rangeIndex, type, TIME_STEP);
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      shiftTime(dayIndex, rangeIndex, type, -TIME_STEP);
    }
  };

  const addTimeRange = (dayIndex) => {
    let showError = null;

    setSchedule((prev) => {
      const updated = [...prev];
      const selectedDay = { ...updated[dayIndex] };

      if (!selectedDay.enabled) {
        showError = `Please enable ${selectedDay.name} before adding a new range.`;
        return prev;
      }

      const ranges = [...selectedDay.dayTimeRanges];
      if (!ranges.length) {
        showError = "No time ranges available.";
        return prev;
      }

      const last = ranges[ranges.length - 1];
      const lastEnd = timeToMinutes(last.end);

      if (Number.isNaN(lastEnd)) {
        showError = "Invalid last end time.";
        return prev;
      }

      const nextStart = lastEnd + MIN_GAP;
      if (nextStart > END_OF_DAY_MINUTES) {
        showError = "Cannot add more ranges — reached end of day.";
        return prev;
      }

      const nextEnd = Math.min(
        nextStart + DEFAULT_RANGE_DURATION,
        END_OF_DAY_MINUTES
      );

      if (nextEnd <= nextStart) {
        showError = "Cannot add more ranges — reached end of day.";
        return prev;
      }

      const newRange = {
        _k: makeKey(),
        start: minutesToTime(nextStart),
        end: minutesToTime(nextEnd),
        isBreak: false,
      };

      const newRanges = [...ranges, newRange];
      const validationError = validateRangesWithBoundaries(newRanges);

      if (validationError) {
        showError = validationError;
        return prev;
      }

      selectedDay.dayTimeRanges = newRanges;
      updated[dayIndex] = selectedDay;
      return updated;
    });

    if (showError) {
      setTimeout(() => toast.error(showError), 0);
    }
  };

  const deleteTimeRange = (dayIndex, rangeIndex) => {
    setSchedule((prev) => {
      const updated = [...prev];
      const selectedDay = { ...updated[dayIndex] };

      if (selectedDay.dayTimeRanges.length <= 1) {
        selectedDay.dayTimeRanges = [
          {
            _k: makeKey(),
            start: defaultTimes.start,
            end: defaultTimes.end,
            isBreak: false,
          },
        ];
      } else {
        selectedDay.dayTimeRanges = selectedDay.dayTimeRanges.filter(
          (_, i) => i !== rangeIndex
        );
      }

      updated[dayIndex] = selectedDay;
      return updated;
    });
  };

  const mergeActiveRanges = (ranges) => {
    const active = ranges.filter((r) => !r.isBreak);
    if (active.length === 0) return [];

    return active.map(({ start, end }) => ({ start, end }));
  };

  const getValidToken = async () => {
    let token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!token) return null;

    const headers = buildHeaders(null);

    const verify = await fetch(`${BASE_URL}/auth/token/verify/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ token }),
    });

    if (verify.ok) return token;

    const refresh =
      localStorage.getItem("refreshToken") ||
      sessionStorage.getItem("refreshToken");

    if (!refresh) return null;

    const res = await fetch(`${BASE_URL}/auth/token/refresh/`, {
      method: "POST",
      headers,
      body: JSON.stringify({ refresh }),
    });

    const data = await res.json().catch(() => ({}));
    if (res.ok && data.access) {
      const useLocalStorage =
        !!localStorage.getItem("accessToken") ||
        !!localStorage.getItem("refreshToken");

      if (useLocalStorage) {
        localStorage.setItem("accessToken", data.access);
      } else {
        sessionStorage.setItem("accessToken", data.access);
      }

      return data.access;
    }

    return null;
  };

  const fetchWorkdays = async () => {
    try {
      const token = await getValidToken();
      if (!token) return;

      const headers = buildHeaders(token);
      const res = await axios.get(WORKDAYS_GET, { headers });
      const data = res.data;

      if (data && data.length > 0) {
        const newSchedule = daysList.map((dayName, index) => {
          const dayData = data.find((d) => d.day === dayName);

          if (dayData) {
            return {
              name: days[index],
              enabled: dayData.active,
              dayTimeRanges:
                dayData.dayTimeRanges.length > 0
                  ? dayData.dayTimeRanges.map((r) => {
                      let startTime = r.start_time;
                      let endTime = r.end_time;

                      if (
                        startTime &&
                        !String(startTime).toLowerCase().includes("am") &&
                        !String(startTime).toLowerCase().includes("pm")
                      ) {
                        const [hours, minutes] = startTime.split(":").map(Number);
                        const date = new Date();
                        date.setHours(hours, minutes, 0, 0);
                        startTime = toPm(date);
                      }

                      if (
                        endTime &&
                        !String(endTime).toLowerCase().includes("am") &&
                        !String(endTime).toLowerCase().includes("pm")
                      ) {
                        const [hours, minutes] = endTime.split(":").map(Number);
                        const date = new Date();
                        date.setHours(hours, minutes, 0, 0);
                        endTime = toPm(date);
                      }

                      return {
                        _k: makeKey(),
                        start: (startTime || defaultTimesApi.start_time).toUpperCase(),
                        end: (endTime || defaultTimesApi.end_time).toUpperCase(),
                        isBreak: false,
                      };
                    })
                  : [
                      {
                        _k: makeKey(),
                        start: defaultTimes.start,
                        end: defaultTimes.end,
                        isBreak: false,
                      },
                    ],
            };
          }

          return {
            name: days[index],
            enabled: false,
            dayTimeRanges: [
              {
                _k: makeKey(),
                start: defaultTimes.start,
                end: defaultTimes.end,
                isBreak: false,
              },
            ],
          };
        });

        setSchedule(newSchedule);
      }
    } catch (err) {
      console.error("Error fetching workdays:", err);
      if (err.response?.status !== 404 && err.response?.status !== 401) {
        toast.error("Error fetching workdays");
      }
    }
  };

  const createOrUpdateWorkdays = async () => {
    try {
      setLoading(true);

      const activeDays = schedule.filter((d) => d.enabled);

      if (userStatus !== "salon" && activeDays.length === 0) {
        toast.error("Please enable at least one working day.");
        return;
      }

      const token = await getValidToken();
      if (!token) {
        toast.error("No access token found. Please log in.");
        navigate("/sign-in", { replace: true });
        return;
      }

      const payload = schedule.map((day) => ({
        day: day.name.toLowerCase(),
        active: day.enabled,
        dayTimeRanges: day.enabled
          ? mergeActiveRanges(day.dayTimeRanges).map(({ start, end }) => ({
              start_time: convertTo24HourFormat(start),
              end_time: convertTo24HourFormat(end),
            }))
          : [],
      }));

      const headers = buildHeaders(token);

      try {
        await axios.put(WORKDAYS_UPDATE, payload, { headers });
      } catch (err) {
        if (err.response?.status === 404) {
          await axios.post(WORKDAYS_CREATE, payload, { headers });
        } else {
          throw err;
        }
      }

      localStorage.removeItem("working_days_closed");
      localStorage.setItem("working_days_saved_time", new Date().toISOString());
      localStorage.setItem("working_days_timezone", userTimeZone);

      if (userStatus === "worker") {
        localStorage.setItem("onboarding_code", "236");
        localStorage.setItem("onboarding_completed", "true");
        sessionStorage.setItem("onboarding_force_route", "/finish-registration");
        sessionStorage.setItem("onboarding_force_status", "worker");

        toast.success("Workdays saved successfully!");
        navigate("/finish-registration", { replace: true });
        return;
      }

      const nextCode = userStatus === "salon" ? "232" : "220";
      localStorage.setItem("onboarding_code", nextCode);

      toast.success("Workdays saved successfully!");
      navigate("/booking-settings", { replace: true });
    } catch (err) {
      console.error("Error saving workdays:", err);

      if (err.response?.status === 400) {
        toast.error("Invalid data format. Please check your time entries.");
      } else if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
      } else if (err.response?.status === 500) {
        toast.error("Server error. Please check your time values and try again.");
      } else {
        toast.error("Error saving working hours. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="working-days-page">
      <div className="working-days-card">
        <div className="working-days-header">
          <div className="working-days-title">
            <h2>Working Days</h2>
            <p>
              {userStatus === "salon"
                ? "Optional for salons — you can skip if not needed."
                : "Pick the days you're available"}
            </p>
          </div>
        </div>

        <div className="working-days-list">
          {schedule.map((day, dayIndex) => (
            <div key={day.name} className="working-days-item">
              <span className="working-days-name">{day.name}</span>

              <div className="working-days-time-multi">
                {day.dayTimeRanges.map((range, rangeIndex) => (
                  <div key={range._k} className="working-days-time">
                    <div className="working-days-input">
                      <input
                        type="time"
                        value={to24(range.start)}
                        disabled={!day.enabled}
                        step={TIME_STEP * 60}
                        onKeyDown={(e) =>
                          handleTimeKeyDown(e, dayIndex, rangeIndex, "start")
                        }
                        onChange={(e) =>
                          handleTimeChange(
                            dayIndex,
                            rangeIndex,
                            "start",
                            e.target.value
                          )
                        }
                      />

                      <div className="working-days-arrows">
                        <button
                          type="button"
                          className="working-days-arrow-btn"
                          disabled={!day.enabled}
                          onClick={() =>
                            shiftTime(dayIndex, rangeIndex, "start", TIME_STEP)
                          }
                        >
                          <FiChevronUp size={12} />
                        </button>
                        <button
                          type="button"
                          className="working-days-arrow-btn"
                          disabled={!day.enabled}
                          onClick={() =>
                            shiftTime(dayIndex, rangeIndex, "start", -TIME_STEP)
                          }
                        >
                          <FiChevronDown size={12} />
                        </button>
                      </div>
                    </div>

                    <span className="working-days-dash">–</span>

                    <div className="working-days-input">
                      <input
                        type="time"
                        value={to24(range.end)}
                        disabled={!day.enabled}
                        step={TIME_STEP * 60}
                        onKeyDown={(e) =>
                          handleTimeKeyDown(e, dayIndex, rangeIndex, "end")
                        }
                        onChange={(e) =>
                          handleTimeChange(
                            dayIndex,
                            rangeIndex,
                            "end",
                            e.target.value
                          )
                        }
                      />

                      <div className="working-days-arrows">
                        <button
                          type="button"
                          className="working-days-arrow-btn"
                          disabled={!day.enabled}
                          onClick={() =>
                            shiftTime(dayIndex, rangeIndex, "end", TIME_STEP)
                          }
                        >
                          <FiChevronUp size={12} />
                        </button>
                        <button
                          type="button"
                          className="working-days-arrow-btn"
                          disabled={!day.enabled}
                          onClick={() =>
                            shiftTime(dayIndex, rangeIndex, "end", -TIME_STEP)
                          }
                        >
                          <FiChevronDown size={12} />
                        </button>
                      </div>
                    </div>

                    {rangeIndex === 0 ? (
                      <button
                        type="button"
                        className="add-time-btn"
                        onClick={() => addTimeRange(dayIndex)}
                        disabled={
                          !day.enabled || !canAddTimeRangeByRanges(day.dayTimeRanges)
                        }
                      >
                        <FiPlus size={16} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="delete-time-btn"
                        onClick={() => deleteTimeRange(dayIndex, rangeIndex)}
                        disabled={!day.enabled}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <label className="working-days-toggle">
                <input
                  type="checkbox"
                  checked={day.enabled}
                  onChange={() => toggleDay(dayIndex)}
                />
                <span className="working-days-slider"></span>
              </label>
            </div>
          ))}
        </div>

        <div className="working-days-footer">
          <button
            className="working-days-next-btn"
            onClick={createOrUpdateWorkdays}
            disabled={loading}
          >
            {loading
              ? "Saving..."
              : userStatus === "salon"
              ? "Skip / Save"
              : "Save"}
          </button>

          <p className="working-days-step">
            {userStatus === "worker" ? "Step 2/2" : "Step 4/5"}{" "}
            {userStatus === "salon" && (
              <span className="text-muted">(optional for salons)</span>
            )}
          </p>
        </div>
      </div>

      <footer className="working-days-footer-info">
        <p>© 2025 Halper. All rights reserved.</p>
        <div className="working-days-footer-links">
          <a href="#">Privacy</a>
          <a href="#">Get help</a>
        </div>
      </footer>
    </div>
  );
}